import { Result } from "../types";

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestConfig {
  path: string;
  method?: HttpMethod;
  params?: Record<string, string | number | undefined>;
  body?: unknown;
  headers?: Record<string, string>;
  /** When set, the global error snackbar middleware does not fire (caller handles UI). */
  skipErrorNotification?: boolean;
}

type RequestExtras = Pick<RequestConfig, 'skipErrorNotification' | 'headers'>;

export interface ApiRequestContext {
  url: string;
  init: RequestInit;
  config: RequestConfig;
}

export interface ApiMiddleware {
  onRequest?: (context: ApiRequestContext) => Promise<ApiRequestContext> | ApiRequestContext;
  onResponse?: (
    response: Response,
    context: ApiRequestContext
  ) => Promise<Response> | Response;
  onError?: (error: ApiError, context: ApiRequestContext) => Promise<void> | void;
}

export interface ApiClientOptions {
  baseUrl?: string;
}

export class ApiError<T = unknown> extends Error {
  status: number;
  data?: T;

  constructor(status: number, message: string, data?: T) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export class ApiClient {
  private baseUrl: string;
  private middlewares: ApiMiddleware[] = [];

  constructor(options?: ApiClientOptions) {
    const envUrl = process.env.REACT_APP_CHAT_APP_URL;
    // envUrl should include protocol (http:// or https://)
    this.baseUrl = options?.baseUrl ?? (envUrl || '');
  }

  use(middleware: ApiMiddleware) {
    this.middlewares.push(middleware);
  }

  async request<T>(config: RequestConfig): Promise<Result<T>> {
    const url = new URL(config.path, this.baseUrl);
    if (config.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const context: ApiRequestContext = {
      url: url.toString(),
      init: {
        method: config.method ?? 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(config.headers ?? {})
        },
        body: config.body !== undefined ? JSON.stringify(config.body) : undefined
      },
      config
    };

    const preparedContext = await this.applyRequestMiddleware(context);

    try {
      const response = await fetch(preparedContext.url, preparedContext.init);
      const processedResponse = await this.applyResponseMiddleware(
        response,
        preparedContext
      );

      if (!processedResponse.ok) {
        const errorPayload = await this.safeReadJson(processedResponse);
        const message = this.resolveErrorMessage(
          errorPayload,
          processedResponse.statusText
        );
        const apiError = new ApiError(processedResponse.status, message, errorPayload);
        await this.applyErrorMiddleware(apiError, preparedContext);
        return { data: null, error: message, success: false };
      }

      const data = await this.safeReadJson(processedResponse);
      return { data: data as T, error: null, success: true };
    } catch (error) {
      const apiError = error instanceof ApiError
        ? error
        : new ApiError(
          0,
          error instanceof Error ? error.message : 'Unexpected API error'
        );

      await this.applyErrorMiddleware(apiError, preparedContext);
      return { data: null, error: apiError.message, success: false };
    }
  }

  get<T>(path: string, params?: RequestConfig['params'], extras?: RequestExtras) {
    return this.request<T>({ path, method: 'GET', params, ...extras });
  }

  post<T>(path: string, body?: unknown, extras?: RequestExtras): Promise<Result<T>> {
    return this.request<T>({ path, method: 'POST', body, ...extras });
  }

  put<T>(path: string, body?: unknown, params?: RequestConfig['params'], extras?: RequestExtras) {
    return this.request<T>({ path, method: 'PUT', body, params, ...extras });
  }

  patch<T>(path: string, body?: unknown, extras?: RequestExtras): Promise<Result<T>> {
    return this.request<T>({ path, method: 'PATCH', body, ...extras });
  }

  delete<T>(path: string) {
    return this.request<T>({ path, method: 'DELETE' });
  }

  private async safeReadJson(response: Response) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  private resolveErrorMessage(errorPayload: unknown, fallbackStatusText?: string): string {
    if (typeof errorPayload === 'string' && errorPayload.trim().length > 0) {
      return errorPayload;
    }

    if (errorPayload && typeof errorPayload === 'object') {
      const payload = errorPayload as Record<string, unknown>;
      const fromMessage = this.stringifyErrorMessage(payload.message);
      if (fromMessage) {
        return fromMessage;
      }
      const fromError = this.stringifyErrorMessage(payload.error);
      if (fromError) {
        return fromError;
      }
    }

    if (fallbackStatusText && fallbackStatusText.trim().length > 0) {
      return fallbackStatusText;
    }

    return 'Unexpected API error';
  }

  private stringifyErrorMessage(value: unknown): string | null {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
    if (Array.isArray(value)) {
      const parts = value
        .map(item => this.stringifyErrorMessage(item))
        .filter((item): item is string => Boolean(item));
      if (parts.length > 0) {
        return parts.join(' ');
      }
    }
    if (value && typeof value === 'object' && 'message' in value) {
      return this.stringifyErrorMessage((value as { message?: unknown }).message);
    }
    return null;
  }

  private async applyRequestMiddleware(context: ApiRequestContext) {
    let current = context;
    for (const middleware of this.middlewares) {
      if (middleware.onRequest) {
        current = await middleware.onRequest(current);
      }
    }
    return current;
  }

  private async applyResponseMiddleware(response: Response, context: ApiRequestContext) {
    let current = response;
    for (const middleware of this.middlewares) {
      if (middleware.onResponse) {
        current = await middleware.onResponse(current, context);
      }
    }
    return current;
  }

  private async applyErrorMiddleware(error: ApiError, context: ApiRequestContext) {
    for (const middleware of this.middlewares) {
      if (middleware.onError) {
        await middleware.onError(error, context);
      }
    }
  }
}

export const createAuthMiddleware = (getToken: () => string | null): ApiMiddleware => ({
  onRequest: context => {
    const token = getToken();
    if (token) {
      context.init.headers = {
        ...(context.init.headers || {}),
        Authorization: `Bearer ${token}`
      };
    }
    return context;
  }
});

export const createJsonResponseMiddleware = (): ApiMiddleware => ({
  onResponse: async response => response
});

export const createUnauthorizedMiddleware = (onUnauthorized: () => void): ApiMiddleware => ({
  onError: error => {
    if (error.status === 401) {
      onUnauthorized();
    }
  }
});

export const createErrorNotificationMiddleware = (
  notify: (message: string) => void
): ApiMiddleware => ({
  onError: (error, context) => {
    if (context.config.skipErrorNotification) {
      return;
    }
    const message =
      error.message && error.message.trim().length > 0
        ? error.message
        : 'Unexpected API error';
    notify(message);
  }
});


